import { useCallback, useMemo, useState } from "react";
import { useStateAwareNavigate } from "@hooks/useStateAwareNavigate";
import { ChartOfAccountsTemplate } from "@common/types/chartOfAccountsTemplate";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { EntityProfileIndustry, EntityProfileType } from "@common/types/entity";
import { ChartOfAccountsTemplatesView } from "@common/types/views/chartOfAccountsTemplatesView";
import { View } from "@common/types/views/views";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { Loader } from "@design-system/Loader/Loader";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { CommandIslandTotal } from "@design-system/CommandIsland/CommandIslandTotal";
import commandIslandStyles from "@design-system/CommandIsland/CommandIslandVariants.module.scss";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { ViewTableMeta } from "@hooks/view/useViewTableMeta";
import {
    useArchiveChartOfAccountsTemplateMutation,
    useChartOfAccountsTemplates,
    useEnableChartOfAccountsTemplateMutation,
} from "@api/coaTemplate.api";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { pluralizeWithCount } from "@common/helpers/string";
import { isErrorToastedByBackendClient } from "@lib/backendClient";
import {
    ChartOfAccountsTemplateRow,
    chartOfAccountsTemplatesViewConfig,
} from "./chartOfAccountsTemplatesViewConfig";
import { ArchiveFilter } from "../templates/archiveFilter";
import { filterChartOfAccountsTemplateRows } from "./chartOfAccountsTemplatesFilters";
import { ChartOfAccountsTemplateOverlays } from "./ChartOfAccountsTemplateOverlays";

interface Props {
    search: string;
    selectedIndustries?: EntityProfileIndustry[];
    selectedBases?: AccountingLedgerBasis[];
    selectedLegalTypes?: EntityProfileType[];
    archiveFilter: ArchiveFilter;
    view: ChartOfAccountsTemplatesView;
    canManage: boolean;
    onViewChange: (view: View) => void;
    onOpenEditor: (template: ChartOfAccountsTemplate) => void;
    onCreateTemplate: () => void;
}

export interface ChartOfAccountsTemplateCallbacks {
    onApplyTemplate: (id: string) => void;
    onEditTemplate: (id: string) => void;
    onEditTemplateAccounts: (id: string) => void;
    onDuplicateTemplate: (id: string) => void;
    onDeleteTemplate: (id: string) => void;
    onArchiveToggleTemplate: (id: string) => void;
    onViewAppliedEntities: (id: string) => void;
    onViewActivity: (id: string) => void;
}

// Extra table meta consumed by the actions cell to gate management controls.
export interface ChartOfAccountsTemplateTableMeta extends ViewTableMeta<
    ChartOfAccountsTemplateCallbacks,
    never
> {
    canManage: boolean;
}

export const ChartOfAccountsTemplatesList = ({
    search,
    selectedIndustries,
    selectedBases,
    selectedLegalTypes,
    archiveFilter,
    view,
    canManage,
    onViewChange,
    onOpenEditor,
    onCreateTemplate,
}: Props) => {
    const navigate = useStateAwareNavigate();
    const { data: templates, isFetched } = useChartOfAccountsTemplates({
        includeArchived: true,
    });

    const { toast: successToast, error: errorToast } = useToaster();
    const archiveTemplate = useArchiveChartOfAccountsTemplateMutation();
    const enableTemplate = useEnableChartOfAccountsTemplateMutation();

    const [applyingTemplate, setApplyingTemplate] =
        useState<ChartOfAccountsTemplate | null>(null);
    const [editingTemplate, setEditingTemplate] =
        useState<ChartOfAccountsTemplate | null>(null);
    const [duplicatingTemplate, setDuplicatingTemplate] =
        useState<ChartOfAccountsTemplate | null>(null);
    const [deletingTemplate, setDeletingTemplate] =
        useState<ChartOfAccountsTemplate | null>(null);
    const [viewingAppliedEntitiesTemplate, setViewingAppliedEntitiesTemplate] =
        useState<ChartOfAccountsTemplate | null>(null);
    const [viewingActivityTemplate, setViewingActivityTemplate] =
        useState<ChartOfAccountsTemplate | null>(null);

    const templatesList = useMemo(() => templates ?? [], [templates]);

    const templatesById = useMemo(
        () => new Map(templatesList.map((t) => [t.id, t])),
        [templatesList],
    );

    const filteredRows = useMemo<ChartOfAccountsTemplateRow[]>(() => {
        const lowerSearch = search.toLowerCase();
        const searchedRows = templatesList
            .filter(
                (t) => !search || t.name.toLowerCase().includes(lowerSearch),
            )
            .map((t) => ({
                id: t.id,
                name: t.name,
                organizationId: t.organizationId,
                basis: t.basis,
                industries: t.industries,
                entityLegalType: t.entityLegalType,
                hasCustomChartOfAccounts: t.hasCustomChartOfAccounts,
                description: t.description,
                appliedEntitiesCount: t.appliedEntitiesCount,
                canDelete: t.canDelete,
                archivedAt: t.archivedAt,
            }));
        return filterChartOfAccountsTemplateRows({
            rows: searchedRows,
            selectedIndustries: selectedIndustries ?? [],
            selectedBases: selectedBases ?? [],
            selectedLegalTypes: selectedLegalTypes ?? [],
            archiveFilter,
        });
    }, [
        templatesList,
        search,
        selectedIndustries,
        selectedBases,
        selectedLegalTypes,
        archiveFilter,
    ]);

    const hasActiveFilters =
        !!search ||
        archiveFilter !== "active" ||
        !!selectedIndustries?.length ||
        !!selectedBases?.length ||
        !!selectedLegalTypes?.length;

    const handleApplyTemplate = useCallback(
        (id: string) => {
            const template = templatesById.get(id);
            if (template) {
                setApplyingTemplate(template);
            }
        },
        [templatesById],
    );

    const handleEditTemplate = useCallback(
        (id: string) => {
            const template = templatesById.get(id);
            if (template) {
                setEditingTemplate(template);
            }
        },
        [templatesById],
    );

    const handleEditTemplateAccounts = useCallback(
        (id: string) => {
            const template = templatesById.get(id);
            if (template) {
                onOpenEditor(template);
            }
        },
        [templatesById, onOpenEditor],
    );

    const handleDuplicateTemplate = useCallback(
        (id: string) => {
            const template = templatesById.get(id);
            if (template) {
                setDuplicatingTemplate(template);
            }
        },
        [templatesById],
    );

    const handleDeleteTemplate = useCallback(
        (id: string) => {
            const template = templatesById.get(id);
            if (template) {
                setDeletingTemplate(template);
            }
        },
        [templatesById],
    );

    const handleArchiveToggleTemplate = useCallback(
        async (id: string) => {
            const template = templatesById.get(id);
            if (!template) {
                return;
            }
            const isArchived = template.archivedAt != null;
            const verb = isArchived ? "enable" : "archive";
            try {
                if (isArchived) {
                    await enableTemplate.mutateAsync(id);
                    successToast(`Enabled template "${template.name}"`);
                } else {
                    await archiveTemplate.mutateAsync(id);
                    successToast(`Archived template "${template.name}"`);
                }
            } catch (error) {
                if (!isErrorToastedByBackendClient(error)) {
                    errorToast(`Failed to ${verb} template "${template.name}"`);
                }
            }
        },
        [
            templatesById,
            archiveTemplate,
            enableTemplate,
            successToast,
            errorToast,
        ],
    );

    const handleAddEntityFromApply = useCallback(() => {
        setApplyingTemplate(null);
        navigate("/new-entity");
    }, [navigate]);

    const handleEditTemplateFromApply = useCallback(() => {
        if (applyingTemplate) {
            setEditingTemplate(applyingTemplate);
        }
        setApplyingTemplate(null);
    }, [applyingTemplate]);

    const handleViewAppliedEntities = useCallback(
        (id: string) => {
            const template = templatesById.get(id);
            if (template) {
                setViewingAppliedEntitiesTemplate(template);
            }
        },
        [templatesById],
    );
    const handleViewActivity = useCallback(
        (id: string) => {
            const template = templatesById.get(id);
            if (template) {
                setViewingActivityTemplate(template);
            }
        },
        [templatesById],
    );

    const tableMeta = useMemo<ChartOfAccountsTemplateTableMeta>(
        () => ({
            canManage,
            callbacks: {
                onApplyTemplate: handleApplyTemplate,
                onEditTemplate: handleEditTemplate,
                onEditTemplateAccounts: handleEditTemplateAccounts,
                onDuplicateTemplate: handleDuplicateTemplate,
                onDeleteTemplate: handleDeleteTemplate,
                onArchiveToggleTemplate: (id) => {
                    void handleArchiveToggleTemplate(id);
                },
                onViewAppliedEntities: handleViewAppliedEntities,
                onViewActivity: handleViewActivity,
            },
        }),
        [
            canManage,
            handleApplyTemplate,
            handleEditTemplate,
            handleEditTemplateAccounts,
            handleDuplicateTemplate,
            handleDeleteTemplate,
            handleArchiveToggleTemplate,
            handleViewAppliedEntities,
            handleViewActivity,
        ],
    );

    return (
        <>
            <ViewTable
                data={isFetched ? filteredRows : null}
                view={view}
                config={chartOfAccountsTemplatesViewConfig}
                onViewChange={onViewChange}
                emptyView={() => {
                    if (hasActiveFilters || templatesList.length > 0) {
                        return <EmptyState header="No templates found" />;
                    }
                    if (!canManage) {
                        return (
                            <EmptyState
                                header="No chart of accounts templates yet"
                                body="Your organization hasn't created any chart of accounts templates."
                            />
                        );
                    }
                    return (
                        <EmptyState
                            header="Create your first CoA template"
                            body="Build templates tailored to any industry, basis, or entity type and apply them across your clients."
                            ctaText="Add template"
                            action={onCreateTemplate}
                        />
                    );
                }}
                loadingView={() => <Loader />}
                tableMeta={tableMeta}
                fixedTopOffsetPx
                enableColumnResizing
                footer={
                    <CommandIsland
                        sticky
                        className={commandIslandStyles.bulkActionsStickyFooter}
                    >
                        {filteredRows.length > 0 ? (
                            <CommandIslandTotal>
                                {pluralizeWithCount(
                                    "template",
                                    filteredRows.length,
                                )}
                            </CommandIslandTotal>
                        ) : undefined}
                    </CommandIsland>
                }
            />

            <ChartOfAccountsTemplateOverlays
                applyingTemplate={applyingTemplate}
                setApplyingTemplate={setApplyingTemplate}
                editingTemplate={editingTemplate}
                setEditingTemplate={setEditingTemplate}
                duplicatingTemplate={duplicatingTemplate}
                setDuplicatingTemplate={setDuplicatingTemplate}
                deletingTemplate={deletingTemplate}
                setDeletingTemplate={setDeletingTemplate}
                viewingAppliedEntitiesTemplate={viewingAppliedEntitiesTemplate}
                setViewingAppliedEntitiesTemplate={
                    setViewingAppliedEntitiesTemplate
                }
                viewingActivityTemplate={viewingActivityTemplate}
                setViewingActivityTemplate={setViewingActivityTemplate}
                onAddEntity={handleAddEntityFromApply}
                onEditTemplateFromApply={handleEditTemplateFromApply}
            />
        </>
    );
};
